import { Alert, Box, Button, Chip, Modal, Snackbar, Stack, Typography } from '@mui/material';

import React, { useEffect, useState } from 'react';

import dayjs from 'dayjs';

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import moment from 'moment';
import { ExportDeviceReport } from '../../../network/Devices';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  p: 4,
  borderRadius: 1,
  width: 400
};

const durations = [
  { id: 1, label: 'Today', start: dayjs().startOf('day'), end: dayjs().endOf('day') },
  {
    id: 2,
    label: 'Last 7 days',
    start: dayjs().subtract(7, 'day').startOf('day'),
    end: dayjs().endOf('day')
  },
  {
    id: 3,
    label: 'Last 1 month',
    start: dayjs().subtract(1, 'month').startOf('day'),
    end: dayjs().endOf('day')
  },
  {
    id: 4,
    label: 'Custom Date',
    start: null,
    end: null
  }
];

const dataTypes = [
  { id: 1, type: 'history', label: 'History' },
  { id: 2, type: 'alert', label: 'Alerts' }
];

export default function DeviceReportExportModal({ openAssignedModal, handleClose, deviceId }) {
  const [alertMetas, setAlertMetas] = useState({ snackBarOpened: false, msg: '', severity: '' });
  const [dataType, setDataType] = useState('');
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const now = dayjs().startOf('day');
  const todayStart = dayjs();

  const [startDate, setStartDate] = useState(now);
  const [endDate, setEndDate] = useState(todayStart);

  const handleSnackBarClose = () => setAlertMetas({ ...alertMetas, snackBarOpened: false });

  useEffect(() => {
    if (endDate < startDate) {
      setStartDate(moment(startDate).endOf('day').valueOf());
    }
  }, [startDate, endDate]);

  const handleStartDateChange = (newValue) => {
    const newStartDate = dayjs(newValue).startOf('day');
    setStartDate(newStartDate);

    // If the new start date is greater than the end date, reset the end date
    if (newStartDate.isAfter(endDate)) {
      setEndDate(newStartDate.endOf('day'));
    }
  };

  const handleEndDateChange = (newValue) => {
    const newEndDate = dayjs(newValue).endOf('day');
    setEndDate(newEndDate);

    // Ensure the end date is not before the start date
    if (newEndDate.isBefore(startDate)) {
      setStartDate(newEndDate.startOf('day'));
    }
  };

  const resetForm = () => {
    setDataType('');
    setSelectedDuration(null);
    setStartDate(now);
    setEndDate(todayStart);
  };

  const exportData = async () => {
    if (!selectedDuration || !dataType) return;

    const start = selectedDuration.id === 4 ? startDate : selectedDuration.start.valueOf();
    const end = selectedDuration.id === 4 ? endDate : selectedDuration.end.valueOf();

    const startDateEpoch = start.valueOf();
    const endDateEpoch = end.valueOf();

    setIsExporting(true);

    try {
      const res = await ExportDeviceReport(deviceId, startDateEpoch, endDateEpoch, dataType);
      if (res) {
        const blobUrl = res?.data?.data;
        const link = document.createElement('a');

        link.href = blobUrl;
        link.setAttribute('download', `${dataType}-report.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      setAlertMetas({
        snackBarOpened: true,
        msg: error?.response?.data?.message || 'Failed to export data',
        severity: 'error'
      });
    } finally {
      setIsExporting(false);
      handleClose();
      resetForm();
    }
  };

  return (
    <Box>
      <Snackbar
        open={alertMetas.snackBarOpened}
        onClose={handleSnackBarClose}
        autoHideDuration={6000}
      >
        <Alert severity={alertMetas.severity}>{alertMetas.msg}</Alert>
      </Snackbar>

      <Modal open={openAssignedModal} onClose={handleClose} data-testid="export-report-modal">
        <Box sx={style} p={4} borderRadius={2} width={400}>
          <Box mb={2}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: '18px', mb: 2 }}>
              Select the data duration
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap" rowGap={1}>
              {durations.map((duration) => (
                <Chip
                  sx={{ borderRadius: '12px' }}
                  key={duration.label}
                  label={duration.label}
                  color={selectedDuration === duration ? 'primary' : 'default'}
                  onClick={() => {
                    setSelectedDuration(duration);
                    if (duration.id !== 4) {
                      setStartDate(duration.start.valueOf());
                      setEndDate(duration.end.valueOf());
                    }
                  }}
                  clickable
                />
              ))}
              {selectedDuration?.id === 4 && (
                <Box display="flex" justifyContent="space-between" gap="10px" mt={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      slotProps={{
                        textField: {
                          sx: {
                            '& .MuiInputBase-input': {
                              fontSize: '12px',
                              fontWeight: '600'
                            }
                          }
                        }
                      }}
                      label="Start Date"
                      value={startDate}
                      onChange={handleStartDateChange}
                      format="DD/MM/YYYY"
                    />
                    <DatePicker
                      slotProps={{
                        textField: {
                          sx: {
                            '& .MuiInputBase-input': {
                              fontSize: '12px',
                              fontWeight: '600'
                            }
                          }
                        }
                      }}
                      label="End Date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      format="DD/MM/YYYY"
                    />
                  </LocalizationProvider>
                </Box>
              )}
            </Stack>
          </Box>
          <Box mb={4} mt={4}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: '18px', mb: 2 }}>
              Select the data for export
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap" rowGap={1}>
              {dataTypes.map((type) => (
                <Chip
                  data-testid={`data-type-chip-${type.type}`}
                  sx={{ borderRadius: '12px' }}
                  key={type.id}
                  label={type.label}
                  color={dataType === type.type ? 'primary' : 'default'}
                  onClick={() => setDataType(type.type)}
                  clickable
                />
              ))}
            </Stack>
          </Box>

          <Box display="flex" justifyContent="end" gap={2}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={exportData}
              variant="contained"
              disabled={isExporting || !dataType || !selectedDuration}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
